import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Document as PdfDocument, Page as PdfPage } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { configurePdfWorker } from './pdfWorker';
import WatermarkOverlay from './WatermarkOverlay';
import './SecureViewer.css';

export type SecureViewerResource = 'document' | 'policy';

interface Meta {
  id: string;
  name?: string;
  title?: string;
  mimeType: string | null;
  conversionStatus: 'native' | 'pending' | 'ready' | 'failed';
}

export interface SecureViewerProps {
  resourceType: SecureViewerResource;
  resourceId: string;
  /** When known (e.g. from /documents list), skips an extra meta round-trip. */
  initialTitle?: string;
  initialConversionStatus?: Meta['conversionStatus'];
  /** Called when the user dismisses the viewer. */
  onClose?: () => void;
  /** When true, render inline (no fixed full-screen overlay). */
  embedded?: boolean;
}

const PAGE_GUTTER = 16;
/** Approximate portrait page height/width — used for off-screen placeholders. */
const PAGE_ASPECT = 1.414;
const LAZY_ROOT_MARGIN = '900px 0px';
const EAGER_PAGE_COUNT = 2;

const isViewableStatus = (
  status: Meta['conversionStatus'] | undefined,
): boolean => status === 'native' || status === 'ready';

/**
 * Full-screen modal viewer that renders a PDF (or PDF-converted Office doc)
 * fetched from the authenticated backend as ArrayBuffer bytes. It applies a
 * layered set of deterrents that DISCOURAGE — but cannot truly prevent — saving
 * and screenshotting:
 *   - watermark overlay carrying the viewer's identity + timestamp
 *   - blur on visibility change / window blur
 *   - swallowed Ctrl+S / Ctrl+P / PrintScreen / F12 key combos
 *   - disabled right-click, copy, cut, drag, text selection
 *   - print stylesheet that blanks the viewer
 *   - no native PDF toolbar (react-pdf renders the page canvas only)
 *
 * Hardline screenshot prevention is impossible in a browser. The watermark is
 * the most effective deterrent: any captured image still carries the viewer's
 * identity, which makes leaks attributable.
 */
const SecureViewer: React.FC<SecureViewerProps> = ({
  resourceType,
  resourceId,
  initialTitle,
  initialConversionStatus,
  onClose,
  embedded = false,
}) => {
  const { user } = useAuth();
  const [meta, setMeta] = useState<Meta | null>(null);
  const [pdfData, setPdfData] = useState<{ data: ArrayBuffer } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadStatus, setLoadStatus] = useState('Loading secure document…');
  const [downloadPercent, setDownloadPercent] = useState<number | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [isBlurred, setIsBlurred] = useState(false);
  const [scale, setScale] = useState(1);
  const [pageWidth, setPageWidth] = useState<number>(0);
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    configurePdfWorker();
  }, []);

  // Lock body scroll while a non-embedded viewer is open.
  useEffect(() => {
    if (embedded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [embedded]);

  // Esc to close (non-embedded only).
  useEffect(() => {
    if (embedded || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [embedded, onClose]);

  // Re-measure the available width so PDF pages always fit the viewport.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const measure = () => {
      const w = node.clientWidth - PAGE_GUTTER * 2;
      setPageWidth(Math.max(320, w));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // Blur the content whenever the page loses focus or visibility — defeats
  // casual "Snip the visible viewer while alt-tabbing" workflows.
  useEffect(() => {
    const blur = () => setIsBlurred(true);
    const unblur = () => setIsBlurred(false);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') blur();
      else unblur();
    };
    window.addEventListener('blur', blur);
    window.addEventListener('focus', unblur);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('blur', blur);
      window.removeEventListener('focus', unblur);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Block save / print / DevTools shortcuts and the PrintScreen key.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      // Ctrl+S, Ctrl+P, Ctrl+Shift+S, Ctrl+Shift+P
      if (ctrl && (key === 's' || key === 'p')) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // F12 and Ctrl+Shift+I (DevTools)
      if (key === 'f12' || (ctrl && e.shiftKey && key === 'i')) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // PrintScreen: try to clear the clipboard right after — best-effort,
      // because by the time we get here the screenshot may already be queued.
      if (key === 'printscreen' || e.code === 'PrintScreen') {
        if (navigator.clipboard?.writeText) {
          void navigator.clipboard.writeText('');
        }
        setIsBlurred(true);
        window.setTimeout(() => setIsBlurred(false), 400);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  // Lightweight DevTools open detector. False positives are possible; we only
  // use it as a soft signal that blurs the content.
  useEffect(() => {
    const threshold = 160;
    let alive = true;
    let raf = 0;
    const check = () => {
      if (!alive) return;
      const widthGap =
        window.outerWidth > 0 ? window.outerWidth - window.innerWidth : 0;
      const heightGap =
        window.outerHeight > 0 ? window.outerHeight - window.innerHeight : 0;
      if (widthGap > threshold || heightGap > threshold) {
        setIsBlurred(true);
      }
      raf = window.setTimeout(check as any, 1500) as unknown as number;
    };
    check();
    return () => {
      alive = false;
      if (raf) window.clearTimeout(raf);
    };
  }, []);

  // Load PDF bytes (meta is skipped when the parent already passed viewable status).
  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setMeta(null);
    setPdfData(null);
    setNumPages(0);
    setDownloadPercent(null);
    setLoadStatus('Loading secure document…');

    const path = resourceType === 'document' ? 'documents' : 'policies';

    const UNAVAILABLE_MESSAGE =
      'This document isn\u2019t available for secure viewing yet. Please ask an administrator to re-upload it.';

    const load = async () => {
      try {
        // Seed header from props; backend /view will self-heal legacy docs.
        setMeta({
          id: resourceId,
          name: initialTitle,
          title: initialTitle,
          mimeType: 'application/pdf',
          conversionStatus: initialConversionStatus ?? 'pending',
        });

        if (!isViewableStatus(initialConversionStatus)) {
          setLoadStatus('Preparing document for secure viewing…');
        }

        void api
          .get<Meta>(`/${path}/${resourceId}/meta`)
          .then((metaRes) => {
            if (!cancelled) setMeta(metaRes.data);
          })
          .catch(() => {
            /* meta is best-effort; /view is authoritative */
          });

        const bytesRes = await api.get<ArrayBuffer>(
          `/${path}/${resourceId}/view`,
          {
            responseType: 'arraybuffer',
            // First open of a legacy Office doc may convert via LibreOffice.
            timeout: 180_000,
            onDownloadProgress: (event) => {
              if (cancelled || !event.total) return;
              const pct = Math.min(
                99,
                Math.round((event.loaded / event.total) * 100),
              );
              setDownloadPercent(pct);
              setLoadStatus(`Downloading document… ${pct}%`);
            },
          },
        );
        if (cancelled) return;

        setPdfData({ data: bytesRes.data });
        setLoadStatus('Preparing pages…');
        // After a successful self-heal, refresh meta so status shows ready/native.
        void api
          .get<Meta>(`/${path}/${resourceId}/meta`)
          .then((metaRes) => {
            if (!cancelled) setMeta(metaRes.data);
          })
          .catch(() => undefined);
      } catch {
        if (cancelled) return;
        setLoadError(UNAVAILABLE_MESSAGE);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [
    resourceType,
    resourceId,
    initialTitle,
    initialConversionStatus,
  ]);

  // Build the per-page identity watermark.
  const watermarkLines = useMemo(() => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate(),
    )} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const name = user?.fullName || 'Unknown viewer';
    const empNo = user?.empNo ? `· ${user.empNo}` : '';
    return [`${name} ${empNo}`.trim(), stamp];
  }, [user?.fullName, user?.empNo]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages: n }: { numPages: number }) => {
      setNumPages(n);
    },
    [],
  );

  const title =
    meta?.name || meta?.title || (resourceType === 'document' ? 'Document' : 'Policy');

  // Container handlers: kill copy/cut/contextmenu/drag at the source.
  const preventEvent = useCallback((e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const rootClass =
    `secure-viewer-root ${embedded ? 'secure-viewer-root--embedded' : ''}` +
    (isBlurred ? ' secure-viewer-root--blurred' : '');

  return (
    <div
      className={rootClass}
      role={embedded ? 'region' : 'dialog'}
      aria-modal={embedded ? undefined : 'true'}
      aria-label={`Secure viewer: ${title}`}
      onContextMenu={preventEvent}
      onCopy={preventEvent}
      onCut={preventEvent}
      onDragStart={preventEvent}
    >
      <div className="secure-viewer-header">
        <div className="secure-viewer-title">
          <span className="secure-viewer-lock" aria-hidden>
            🔒
          </span>
          <span className="secure-viewer-title-text" title={title}>
            {title}
          </span>
          <span className="secure-viewer-badge">Read-only · No download</span>
        </div>
        <div className="secure-viewer-controls">
          <button
            type="button"
            className="secure-viewer-btn"
            onClick={() => setScale((s) => Math.max(0.5, +(s - 0.1).toFixed(2)))}
            aria-label="Zoom out"
          >
            −
          </button>
          <span className="secure-viewer-zoom">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            className="secure-viewer-btn"
            onClick={() => setScale((s) => Math.min(3, +(s + 0.1).toFixed(2)))}
            aria-label="Zoom in"
          >
            +
          </button>
          {onClose && (
            <button
              type="button"
              className="secure-viewer-close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div
        className="secure-viewer-body"
        ref={(node) => {
          containerRef.current = node;
          setScrollRoot(node);
        }}
      >
        {loadError ? (
          <div className="secure-viewer-error" role="alert">
            {loadError}
          </div>
        ) : !pdfData ? (
          <div className="secure-viewer-loading" role="status">
            {downloadPercent != null
              ? `Downloading document… ${downloadPercent}%`
              : loadStatus}
          </div>
        ) : (
          <PdfDocument
            file={pdfData}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(err) =>
              setLoadError(err?.message || 'Failed to render document')
            }
            loading={
              <div className="secure-viewer-loading">{loadStatus}</div>
            }
            error={
              <div className="secure-viewer-error" role="alert">
                Could not render this document.
              </div>
            }
          >
            {Array.from({ length: numPages }, (_, i) => i + 1).map((p) => (
              <LazySecurePage
                key={p}
                pageNumber={p}
                width={pageWidth ? pageWidth * scale : undefined}
                watermarkLines={watermarkLines}
                scrollRoot={scrollRoot}
                eager={p <= EAGER_PAGE_COUNT}
              />
            ))}
          </PdfDocument>
        )}
      </div>

      {isBlurred && (
        <div className="secure-viewer-blur-notice" role="status">
          Viewer hidden — return focus to continue.
        </div>
      )}
    </div>
  );
};

interface LazySecurePageProps {
  pageNumber: number;
  width?: number;
  watermarkLines: string[];
  scrollRoot: HTMLDivElement | null;
  eager?: boolean;
}

/**
 * Renders a PDF page only when it is near the viewport so large documents
 * do not block the UI by painting every page at once.
 */
const LazySecurePage: React.FC<LazySecurePageProps> = ({
  pageNumber,
  width,
  watermarkLines,
  scrollRoot,
  eager = false,
}) => {
  const slotRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(eager);

  useEffect(() => {
    if (visible) return;
    const root = scrollRoot;
    const target = slotRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { root, rootMargin: LAZY_ROOT_MARGIN, threshold: 0.01 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [visible, scrollRoot]);

  const placeholderHeight = width
    ? Math.round(width * PAGE_ASPECT)
    : 720;

  return (
    <div
      ref={slotRef}
      className="secure-viewer-page-wrap"
      style={visible ? undefined : { minHeight: placeholderHeight }}
    >
      {visible ? (
        <SecurePage
          pageNumber={pageNumber}
          width={width}
          watermarkLines={watermarkLines}
        />
      ) : (
        <div className="secure-viewer-page-placeholder" aria-hidden />
      )}
    </div>
  );
};

interface SecurePageProps {
  pageNumber: number;
  width?: number;
  watermarkLines: string[];
}

/** A PDF page wrapped with a watermark overlay sized to the rendered canvas. */
const SecurePage: React.FC<SecurePageProps> = ({
  pageNumber,
  width,
  watermarkLines,
}) => {
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  return (
    <div className="secure-viewer-page-wrap">
      <PdfPage
        pageNumber={pageNumber}
        width={width}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        onRenderSuccess={(page: any) => {
          const w = page?.width ?? width ?? 0;
          const h = page?.height ?? 0;
          if (w && h) setSize({ w, h });
        }}
      />
      {size && (
        <WatermarkOverlay
          lines={watermarkLines}
          width={size.w}
          height={size.h}
        />
      )}
    </div>
  );
};

export default SecureViewer;
